import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { Button } from "@/components/ui/button";
import { Leaf, Moon, Sun, LogOut, ShoppingBag, Boxes, ReceiptText, BarChart3, ClipboardList, HandCoins, Truck, Banknote } from "lucide-react";

const tabs = [
  { to: "/", label: "POS Billing", icon: ShoppingBag, testid: "nav-pos-link", end: true },
  { to: "/inventory", label: "Catalog", icon: Boxes, testid: "nav-inventory-link" },
  { to: "/history", label: "Bill History", icon: ReceiptText, testid: "nav-history-link" },
];

const liveTabs = [
  { to: "/live/orders", label: "Orders", icon: ClipboardList, testid: "nav-live-orders-link" },
  { to: "/live/collections", label: "Collections", icon: HandCoins, testid: "nav-live-collections-link" },
  { to: "/live/operations", label: "Operations", icon: Truck, testid: "nav-live-operations-link" },
  { to: "/cash", label: "Caja", icon: Banknote, testid: "nav-cash-link" },
];

export const Navbar = () => {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin";

  const allTabs = isAdmin
    ? [...tabs, ...liveTabs, { to: "/analytics", label: "Analytics", icon: BarChart3, testid: "nav-analytics-link" }]
    : tabs;

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <header
      data-testid="main-navbar"
      className="sticky top-0 z-50 w-full backdrop-blur-xl bg-background/90 border-b border-border px-3 sm:px-5 lg:px-8 py-2.5 sm:py-3 flex flex-wrap md:flex-nowrap items-center justify-between gap-2 md:gap-4"
    >
      <div className="flex items-center gap-2 shrink-0">
        <div className="h-9 w-9 rounded-xl bg-primary flex items-center justify-center">
          <Leaf className="h-5 w-5 text-primary-foreground" />
        </div>
        <div className="hidden sm:block leading-tight">
          <p className="font-display font-extrabold text-primary text-lg">Verdant<span className="text-accent">POS</span></p>
          <p className="text-[10px] text-muted-foreground -mt-1">by LabAroid</p>
        </div>
      </div>

      <nav className="order-3 md:order-none w-full md:w-auto md:flex-1 flex items-stretch md:items-center md:justify-center gap-1 overflow-x-auto overscroll-x-contain pb-0.5 md:pb-0 scrollbar-none" aria-label="Navegación principal">
        {allTabs.map((t) => (
          <NavLink
            key={t.to}
            to={t.to}
            end={t.end}
            data-testid={t.testid}
            aria-label={t.label}
            title={t.label}
            className={({ isActive }) =>
              `flex shrink-0 flex-col lg:flex-row items-center justify-center gap-1 lg:gap-1.5 min-w-[4.5rem] lg:min-w-10 min-h-12 lg:min-h-10 px-2 lg:px-3 py-2 rounded-lg text-xs lg:text-sm font-semibold whitespace-nowrap transition-colors ${
                isActive
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:bg-secondary hover:text-foreground"
              }`
            }
          >
            <t.icon className="h-4 w-4 shrink-0" />
            <span className="text-[10px] sm:text-[11px] lg:text-sm leading-none lg:leading-normal">{t.label}</span>
          </NavLink>
        ))}
      </nav>

      <div className="flex items-center gap-1 sm:gap-2 shrink-0">
        <span className="hidden lg:inline-flex text-xs font-semibold px-2.5 py-1 rounded-full bg-secondary text-secondary-foreground capitalize">
          {user?.role}
        </span>
        <Button variant="ghost" size="icon" onClick={toggle} data-testid="theme-toggle-button" aria-label="Cambiar tema">
          {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
        </Button>
        <Button variant="outline" size="sm" onClick={doLogout} data-testid="logout-button" className="gap-1.5 px-2.5 sm:px-3">
          <LogOut className="h-4 w-4" />
          <span className="hidden sm:inline">Logout</span>
        </Button>
      </div>
    </header>
  );
};
